import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, errorMessage } from "../lib/api";
import { Alert, Button, Field } from "../components/Form";
import { PageHeader } from "../components/FeatureCard";
import { t } from "../lib/i18n";
import { GatewayPay } from "../components/GatewayPay";
import { useMe, type Me } from "../hooks/useMe";

type Order = {
  reference: string;
  email: string;
  tier: "early_bird_1" | "early_bird_2" | "standard";
  priceSen: number;
  amountDueSen: number;
  status: "pending" | "awaiting_verification" | "paid" | "failed" | "expired";
  holdExpiresAt: string;
};
type Payment = { gateway?: "stripe" };

const STORE_KEY = "pp_checkout";

function remember(v: { ref: string; email: string } | null) {
  try {
    if (v) localStorage.setItem(STORE_KEY, JSON.stringify(v));
    else localStorage.removeItem(STORE_KEY);
  } catch {
    /* storage unavailable — checkout still works, just not across reloads */
  }
}
function recall(): { ref: string; email: string } | null {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) ?? "null");
  } catch {
    return null;
  }
}

/** Shows enough to recognise your own address, not enough to read someone else's (a***@yahoo.com). */
function maskEmail(email: string): string {
  const [name = "", domain = ""] = email.split("@");
  return `${name.slice(0, 1)}***@${domain}`;
}

type Signed = NonNullable<Me>;

/** /beli: sign in first (email link), set a password once, then straight to Stripe. The licence activates itself after payment. */
export function CheckoutPage() {
  const [params] = useSearchParams();
  const { code } = useParams();
  const referral = (code ?? params.get("ref") ?? undefined)?.toUpperCase();
  const returning = params.get("bayar") === "1";
  const { me, loading, reload } = useMe();
  if (loading) return <p className="text-sm text-muted">{t("Memuatkan…")}</p>;
  if (!me) return <SignUpStart referral={referral} />;
  if (me.licenseActive) return <LicenseActive />;
  if (returning) return <Confirming reload={reload} />;
  return <CheckoutFlow me={me} referralCode={referral} />;
}

function SignUpStart({ referral }: { referral?: string | undefined }) {
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const next = referral ? `/beli?ref=${referral}` : "/beli";
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await api("/api/auth/login", { method: "POST", json: { email: f.get("email"), next } });
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="mx-auto max-w-md">
      <PageHeader art="cover/akaun" title={t("Beli JomCerdik Lifetime")} subtitle={t("Masukkan emel anda. Kami hantar pautan pengesahan, kemudian anda tetapkan kata laluan dan terus ke pembayaran.")} />
      {referral && <Alert kind="info">{t("Kod referral: {code}", { code: referral })}</Alert>}
      {sent ? (
        <Alert kind="success">{t("Semak emel anda dan klik pautan untuk teruskan ke pembayaran. Pautan sah selama 15 minit.")}</Alert>
      ) : (
        <form onSubmit={submit} className="mt-4 space-y-4">
          <Field label={t("Emel")} name="email" type="email" required autoComplete="email" hint={t("Lesen akan diikat pada emel ini.")} />
          {error && <Alert kind="error">{error}</Alert>}
          <Button type="submit" busy={busy} className="w-full">{t("Hantar pautan pengesahan")}</Button>
          <Link to={`/login?next=${encodeURIComponent(next)}`} className="block text-center text-sm font-semibold text-brand underline">{t("Sudah ada akaun? Log masuk")}</Link>
        </form>
      )}
    </div>
  );
}

function LicenseActive() {
  return (
    <div className="mx-auto max-w-md space-y-4">
      <PageHeader title={t("Lesen anda sudah aktif")} subtitle={t("Terima kasih! Semua modul boleh digunakan sekarang.")} />
      <Link to="/belajar" className="inline-block rounded-lg bg-brand px-5 py-3 font-semibold text-white">{t("Mula belajar")}</Link>
    </div>
  );
}

/** Back from Stripe: the webhook/return URL confirms the order, and the next /api/me activates the licence. Keep checking a while. */
function Confirming({ reload }: { reload: () => Promise<void> | void }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (n > 40) return;
    const id = setTimeout(() => { void reload(); setN((x) => x + 1); }, 3000);
    return () => clearTimeout(id);
  }, [n, reload]);
  return (
    <div className="mx-auto max-w-md space-y-4">
      <PageHeader title={t("Menyemak bayaran…")} subtitle={t("Sebentar sahaja.")} />
      <Alert kind="info">{t("Kami sedang mengesahkan bayaran anda dengan Stripe. Halaman ini dikemas kini sendiri — jangan tutup.")}</Alert>
      {n > 40 && <Alert kind="error">{t("Belum nampak lesen? Muat semula halaman ini, atau hubungi kami dengan emel akaun anda.")}</Alert>}
    </div>
  );
}

function CheckoutFlow({ me, referralCode }: { me: Signed; referralCode?: string | undefined }) {
  const [params] = useSearchParams();
  const [order, setOrder] = useState<Order | null>(null);
  const [payment, setPayment] = useState<Payment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Back from the payment page (?bayar=1): the callback may land a few seconds later, so keep checking.
  const returning = params.get("bayar") === "1";

  // Resume an in-progress checkout after reload. A finished order (paid / expired / failed) is only shown right after
  // paying (?bayar=1); on any other visit it is forgotten so the buyer gets a clean form and nobody's old email appears.
  useEffect(() => {
    const saved = recall();
    if (!saved) return;
    api<{ order: Order; payment: Payment }>(`/api/orders/${saved.ref}?email=${encodeURIComponent(saved.email)}`)
      .then((r) => {
        if (["paid", "expired", "failed"].includes(r.order.status) && !returning) { remember(null); return; }
        setOrder(r.order);
        setPayment(r.payment);
      })
      .catch(() => remember(null));
  }, []);
  useEffect(() => {
    if (!returning || !order || order.status !== "pending") return;
    let n = 0;
    const id = setInterval(() => {
      if (++n > 20) return clearInterval(id);
      api<{ order: Order }>(`/api/orders/${order.reference}?email=${encodeURIComponent(order.email)}`)
        .then((r) => setOrder(r.order))
        .catch(() => undefined);
    }, 3000);
    return () => clearInterval(id);
  }, [returning, order?.status, order?.reference, order?.email]);

  // Poll while waiting for admin verification.
  useEffect(() => {
    if (order?.status !== "awaiting_verification") return;
    const id = setInterval(() => {
      api<{ order: Order }>(`/api/orders/${order.reference}?email=${encodeURIComponent(order.email)}`)
        .then((r) => setOrder(r.order))
        .catch(() => undefined);
    }, 20000);
    return () => clearInterval(id);
  }, [order?.status, order?.reference, order?.email]);

  async function start(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    try {
      let visitorKey: string | undefined;
      try {
        visitorKey = localStorage.getItem("pp_visitor") ?? crypto.randomUUID();
        localStorage.setItem("pp_visitor", visitorKey);
      } catch {
        visitorKey = undefined;
      }
      if (!me.hasPassword) await api("/api/me/password", { method: "POST", json: { password: f.get("password") } });
      const r = await api<{ order: Order; payment: Payment }>("/api/orders", {
        method: "POST",
        json: { email: me.email, referralCode, visitorKey, acceptTerms: f.get("terms") === "on" },
      });
      setOrder(r.order);
      setPayment(r.payment);
      remember({ ref: r.order.reference, email: r.order.email });
      // Online payment available: go straight to Stripe (the page below stays as a fallback if this fails or they come back).
      if (r.payment.gateway) {
        const c = await api<{ url: string }>(`/api/orders/${r.order.reference}/checkout`, { method: "POST", json: { email: r.order.email } });
        window.location.href = c.url;
        return;
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (!order) {
    return (
      <div className="mx-auto max-w-md">
        <PageHeader art="cover/akaun" title={t("Beli JomCerdik Lifetime")} subtitle={t("Satu lesen, semua modul, akses seumur hidup.")} />
        {referralCode && <Alert kind="info">{t("Kod referral: {code}", { code: referralCode })}</Alert>}
        <form onSubmit={start} className="mt-4 space-y-4">
          <p className="text-sm text-muted">{t("Akaun: {email}", { email: me.email })}</p>
          {!me.hasPassword && <Field label={t("Tetapkan kata laluan")} name="password" type="password" required minLength={8} maxLength={128} autoComplete="new-password" hint={t("Sekurang-kurangnya 8 aksara. Anda guna ini untuk log masuk lain kali.")} />}
          <label className="flex gap-2 text-sm">
            <input type="checkbox" name="terms" required className="mt-1" />
            <span>
              {t("Saya bersetuju dengan")} <Link to="/terma" className="underline">{t("Terma")}</Link>,{" "}
              <Link to="/privasi" className="underline">{t("Privasi")}</Link> {t("dan")} <Link to="/refund" className="underline">{t("Polisi Refund")}</Link>.
            </span>
          </label>
          {error && <Alert kind="error">{error}</Alert>}
          <Button type="submit" busy={busy} className="w-full">
            {t("Teruskan ke bayaran")}
          </Button>
        </form>
      </div>
    );
  }

  if (order.status === "paid") {
    return (
      <div className="mx-auto max-w-md space-y-4">
        <PageHeader title={t("Bayaran disahkan")} subtitle={t("Pesanan {ref}", { ref: order.reference })} />
        <Alert kind="success">{t("Pautan pengaktifan lesen telah dihantar ke {email}. Semak inbox, Spam dan Promosi.", { email: maskEmail(order.email) })}</Alert>
        <Button variant="secondary" onClick={() => { remember(null); setOrder(null); }}>{t("Selesai")}</Button>
      </div>
    );
  }

  if (order.status === "expired" || order.status === "failed") {
    return (
      <div className="mx-auto max-w-md space-y-4">
        <PageHeader title={order.status === "expired" ? t("Tempoh pesanan tamat") : t("Bayaran tidak dapat disahkan")} subtitle={t("Pesanan {ref}", { ref: order.reference })} />
        <Alert kind="error">
          {order.status === "expired"
            ? t("Pesanan ini tamat kerana tiada bayaran direkodkan. Jika anda sudah bayar, hubungi kami dengan kod rujukan ini.")
            : t("Kami tidak menjumpai bayaran yang sepadan. Hubungi kami dengan kod rujukan dan bukti bayaran.")}
        </Alert>
        <Button onClick={() => { remember(null); setOrder(null); }}>{t("Buat pesanan baharu")}</Button>
      </div>
    );
  }

  if (payment?.gateway && order.status === "pending" && returning) {
    return (
      <div className="mx-auto max-w-md space-y-4">
        <PageHeader title={t("Menyemak bayaran…")} subtitle={t("Pesanan {ref}", { ref: order.reference })} />
        <Alert kind="info">{t("Kami sedang mengesahkan bayaran anda dengan Stripe. Halaman ini dikemas kini sendiri — jangan tutup.")}</Alert>
        <GatewayPay reference={order.reference} email={order.email} />
      </div>
    );
  }

  if (order.status === "awaiting_verification") {
    // Legacy orders that were paid by DuitNow QR before Stripe became the only method are still verified by hand.
    return (
      <div className="mx-auto max-w-md space-y-4">
        <PageHeader title={t("Menyemak bayaran…")} subtitle={t("Pesanan {ref}", { ref: order.reference })} />
        <Alert kind="info">
          {t("Terima kasih! Bayaran anda sedang disemak. Pautan pengaktifan akan dihantar ke {email} sebaik sahaja disahkan (biasanya dalam masa beberapa jam pada waktu bekerja). Halaman ini dikemas kini secara automatik. Emel kadang-kadang masuk ke folder Spam atau Promosi, jadi sila semak di situ juga.", { email: maskEmail(order.email) })}
        </Alert>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md space-y-4">
      <PageHeader title={t("Beli JomCerdik Lifetime")} subtitle={t("Pesanan {ref}", { ref: order.reference })} />
      {payment?.gateway && order.status === "pending" ? (
        <GatewayPay reference={order.reference} email={order.email} />
      ) : (
        <Alert kind="error">{t("Pembayaran dalam talian tidak tersedia buat masa ini. Sila cuba lagi sebentar nanti.")}</Alert>
      )}
    </div>
  );
}
